import { withAsyncContext as _withAsyncContext, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createSlots as _createSlots, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-3" }
const _hoisted_2 = { class: "text-primary font-semibold capitalize text-sm" }
const _hoisted_3 = { class: "text-neutral-500 dark:text-neutral-400 mb-4" }
const _hoisted_4 = ["datetime"]
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "prose" }
const _hoisted_7 = { class: "flex flex-row items-center justify-end p-4 gap-x-4" }
const _hoisted_8 = { class: "text-lg leading-lg font-semibold" }
const _hoisted_9 = {
  key: 0,
  class: "text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-4"
}
const _hoisted_10 = { class: "space-y-6 mt-6" }

import type { SanityDocument } from "@sanity/client";
import imageUrlBuilder from "@sanity/image-url";
import type { SanityImageSource } from "@sanity/image-url/lib/types/types";
import { h } from "vue";
import { LazySanityLink } from "#components";

const query = `{
  "previous": *[_type == "post" && publishedAt < $publishedAt && publishedAt <= now()]
    | order(publishedAt desc) [0] { title, slug, description },
  "next": *[_type == "post" && publishedAt > $publishedAt && publishedAt <= now()]
    | order(publishedAt asc) [0] { title, slug, description }
}`;


export default /*@__PURE__*/_defineComponent({
  __name: '[slug]',
  async setup(__props) {

let __temp: any, __restore: any

definePageMeta({
  layout: "blog",
});

const config = useRuntimeConfig();

const POST_QUERY = groq`
  *[_type == "post" && slug.current == $slug][0]{
    _id,
    title,
    slug,
    image,
    publishedAt,
    description,
    body,
    category->{
      label
    },
    author->{
      name,
      image {
        asset->{
          url
        }
      }
    }
  }
`;

const { params, fullPath } = useRoute();
const slug = params.slug;

const currentUrl = computed(() => {
  try {
    return `${config.public.siteUrl || "https://familyaxis.com"}${fullPath}`;
  } catch (error) {
    console.warn("Error generating currentUrl:", error);
    return "https://familyaxis.com";
  }
});

const { data: post } = (
  ([__temp,__restore] = _withAsyncContext(() => useSanityQuery<SanityDocument>(POST_QUERY, params))),
  __temp = await __temp,
  __restore(),
  __temp
);

const preprocessContent = (blocks) => {
  const processed = blocks.map((block, _index) => {
    if (block._type === "block" && block.style?.startsWith("h") && !block.listItem) {
      return { ...block, id: block._key };
    }
    return block;
  });

  // Insert ad after 3rd paragraph (or however you'd like)
  const insertAt = 3;
  if (processed.length > insertAt) {
    processed.splice(insertAt, 0, {
      _type: "adsense",
      _key: "ad-block-middle",
    });
  }

  return processed;
};

defineOgImage("MainOg", {
  title: () => post.value.title,
  description: () => post.value.description,
  theme: "#f5f5f5",
  colorMode: "light",
});

useSeoMeta({
  title: () => post.value.title,
  description: () => post.value.description,
});

const postBody = ref(preprocessContent(post.value.body));

watch(postBody.value, (value) => {
  console.log(value);
});

const readingTime = computed(() => calculateReadingTime(post.value?.body));

const { projectId, dataset } = useSanity().client.config();
const urlFor = (source: SanityImageSource) =>
  projectId && dataset ? imageUrlBuilder({ projectId, dataset }).image(source) : null;

const toc = ref(null);
function generateToc(blocks) {
  const process = unref(blocks)
    .filter((block) => block.style?.match(/^h[1-6]$/)) // Only get headings (h1-h6)
    .map((block) => ({
      id: `h-${block._key}`, // Use _key as the unique ID
      text: block.children.map((child) => child.text).join(" "), // Extract heading text
      depth: parseInt(block.style.replace("h", ""), 10), // Extract heading level
      children: [],
    }));
  return process;
}

if (post?.value.body) {
  toc.value = generateToc(post?.value.body);
}

const crumbs = [
  {
    label: "Blog",
    icon: "i-lucide:newspaper",
    to: "/blog",
  },
  {
    label: slug,
    // icon: 'i-heroicons-document',
    to: `/blog/${slug}`,
  },
];

const { data: surround } = (
  ([__temp,__restore] = _withAsyncContext(async () => useAsyncData("surround", async () => {
  const { publishedAt } = post.value;
  const { data }: any = await useSanityQuery(query, { publishedAt });
  const surroundArticles = [];

  if (!data?.value?.previous?.title) {
    surroundArticles.push({
      title: "Back to blog",
      description: "Check all the articles of FamilyAxis",
      path: "/blog",
    });
  } else {
    surroundArticles.push({
      title: data.value.previous.title,
      description: data.value.previous.description.slice(0, 100),
      path: `/blog/${data.value.previous.slug.current}`,
    });
  }

  if (data?.value?.next?.title) {
    surroundArticles.push({
      title: data.value.next.title,
      description: data.value.next.description.slice(0, 100),
      path: `/blog/${data.value.next.slug.current}`,
    });
  } else {
    surroundArticles.push({
      title: "Back to blog",
      description: "Check all the articles of FamilyAxis",
      path: "/blog",
    });
  }

  return surroundArticles;
}))),
  __temp = await __temp,
  __restore(),
  __temp
);

const socialLinks = computed(() => {
  try {
    const url = currentUrl.value || "";
    const title = post.value?.title || "Check this article!";

    return [
      {
        label: "Share on X",
        icon: "logos:x",
        to: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
      },
      {
        label: "Share on Facebook",
        icon: "logos:facebook",
        to: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      },
      {
        label: "Share on Reddit",
        icon: "logos:reddit-icon",
        to: `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
      },
      {
        label: "Share on Bluesky",
        icon: "logos:bluesky",
        to: `https://bsky.app/intent/compose?${encodeURIComponent(title + " " + url)}`,
      },
    ];
  } catch (error) {
    console.warn("Error generating social links:", error);
    return [];
  }
});

function wrapHeading({ value }, { slots }, tag) {
  const headingId = `h-${value.id || value._key}`;

  // Modular type scale (1.25 ratio) with font-display (Public Sans)
  // Weight differentiation creates visual hierarchy beyond size
  const headingClasses = {
    h1: "font-display text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] mt-12 mb-6",
    h2: "font-display text-3xl md:text-4xl font-bold tracking-tight leading-[1.15] mt-10 mb-5",
    h3: "font-display text-2xl md:text-3xl font-semibold tracking-tight leading-[1.2] mt-8 mb-4",
    h4: "font-display text-xl md:text-2xl font-medium tracking-normal leading-[1.3] mt-6 mb-3",
  };

  return h(
    tag,
    {
      id: headingId,
      class: `scroll-mt-[calc(48px+48px+var(--header-height))] lg:scroll-mt-[calc(48px+var(--header-height))] relative ${headingClasses[tag]}`,
    },
    [
      h(
        "a",
        {
          "aria-current": "page",
          href: `/blog/${slug}#${headingId}`,
          class:
            "group focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ui-primary)] focus-visible:ring-offset-2 rounded-sm",
        },
        [
          h(
            "div",
            {
              class:
                "-ml-7 pr-2 py-2 inline-flex absolute translate-x-0 opacity-0 group-hover:lg:opacity-100 group-focus-visible:opacity-100 transition-all duration-200",
            },
            [
              h("span", {
                class: "iconify i-heroicons:hashtag-20-solid w-4 h-4 text-[var(--ui-primary)]",
                "aria-hidden": true,
              }),
            ],
          ),
          slots.default?.(),
        ],
      ),
    ],
  );
}

const portableTextComponents = {
  block: {
    h1: (props, context) => wrapHeading(props, context, "h1"),
    h2: (props, context) => wrapHeading(props, context, "h2"),
    h3: (props, context) => wrapHeading(props, context, "h3"),
    h4: (props, context) => wrapHeading(props, context, "h4"),
    blockquote: (_, { slots }) => {
      return h("blockquote", null, slots.default?.());
    },
    // normal: (props) => wrapNormal(props),
  },
  marks: {
    // link:  (props) => wrapLinks(props),
    link: ({ value }, { slots }) => {
      return h(LazySanityLink, { href: value.href }, slots.default?.());
    },
    internalLink: ({ value }, { slots }) => {
      return h(LazySanityLink, { href: value.href }, slots.default?.());
    },
  },
  types: {
    image: ({ value }) => {
      return h("img", {
        src: urlFor(value).url(),
        loading: "lazy",
        alt: value.alt || "Article image",
        class: "rounded-xl my-8 mx-auto max-w-full",
      });
    },
  },
};

const showAd = ref(false);

// SSR-safe date formatting function
const formatBlogDate = (dateString: string) => {
  try {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const day = date.getDate();
    const month = date.toLocaleDateString("en-GB", { month: "long" });
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  } catch (error) {
    console.warn("Error formatting date:", error);
    return "";
  }
};

// Use nextTick to ensure proper hydration
onMounted(() => {
  nextTick(() => {
    try {
      showAd.value = true;
    } catch (error) {
      console.warn("Error setting showAd:", error);
    }
  });
});

// Links variable removed - not used

return (_ctx: any,_cache: any) => {
  const _component_UBreadcrumb = _resolveComponent("UBreadcrumb")!
  const _component_Motion = _resolveComponent("Motion")!
  const _component_UPageHeader = _resolveComponent("UPageHeader")!
  const _component_SanityContent = _resolveComponent("SanityContent")!
  const _component_UAvatar = _resolveComponent("UAvatar")!
  const _component_BlogArticleSurround = _resolveComponent("BlogArticleSurround")!
  const _component_UPageBody = _resolveComponent("UPageBody")!
  const _component_USeparator = _resolveComponent("USeparator")!
  const _component_UPageLinks = _resolveComponent("UPageLinks")!
  const _component_BlogToc = _resolveComponent("BlogToc")!
  const _component_UPageAside = _resolveComponent("UPageAside")!
  const _component_UPage = _resolveComponent("UPage")!

  return (_unref(post))
    ? (_openBlock(), _createBlock(_component_UPage, {
        key: 0,
        ui: {
      // center: 'lg:col-span-10',
      // right: 'lg:col-span-2',
    },
        class: "lg:gap-8"
      }, _createSlots({
        default: _withCtx(() => [
          _createVNode(_component_UPageHeader, { class: "w-full pr-16 border border-primary-100 dark:border-slate-800 gap-y-10 mt-10 px-14 bg-[linear-gradient(to_bottom,transparent_0%_1.3em,theme(colors.primary.100)_1.4em),linear-gradient(to_right,transparent_0%_1.3em,theme(colors.primary.200)_1.4em)] dark:bg-[linear-gradient(to_bottom,transparent_0%_1.3em,theme(colors.slate.800)_1.4em),linear-gradient(to_right,transparent_0%_1.3em,theme(colors.slate.800)_1.4em)] bg-[length:1.4em_1.4em]" }, _createSlots({
            headline: _withCtx(() => [
              _createVNode(_component_UBreadcrumb, {
                divider: "/",
                items: 
            crumbs.map((crumb) => ({
              ...crumb,
              label: Array.isArray(crumb.label) ? crumb.label[0] : crumb.label,
            }))
          
              }, null, 8, ["items"])
            ]),
            title: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("span", _hoisted_2, _toDisplayString(_unref(post).category.label), 1),
                _createElementVNode("span", null, _toDisplayString(_unref(post).title), 1)
              ])
            ]),
            description: _withCtx(() => [
              _createElementVNode("p", _hoisted_3, [
                _createElementVNode("time", {
                  datetime: _unref(post).publishedAt
                }, _toDisplayString(formatBlogDate(_unref(post).publishedAt)), 9, _hoisted_4),
                (_unref(readingTime) > 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_5, " · " + _toDisplayString(_unref(readingTime)) + " min read", 1))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("p", null, _toDisplayString(_unref(post).description), 1)
            ]),
            _: 2
          }, [
            (_unref(post).image)
              ? {
                  name: "links",
                  fn: _withCtx(() => [
                    _createVNode(_component_Motion, {
                      as: "img",
                      src: urlFor(_unref(post).image).width(800).height(400).url(),
                      initial: { opacity: 0, scale: 0.95 },
                      animate: { opacity: 1, scale: 1 },
                      transition: { duration: 0.6, ease: [0.25, 1, 0.5, 1] },
                      class: "w-full rounded-xl mt-4",
                      alt: _unref(post).title
                    }, null, 8, ["src", "alt"])
                  ]),
                  key: "0"
                }
              : undefined
          ]), 1024),
          _createVNode(_component_UPageBody, { class: "space-y-[1em]" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_6, [
                (_unref(post).body)
                  ? (_openBlock(), _createBlock(_component_SanityContent, {
                      key: 0,
                      value: _unref(postBody),
                      components: portableTextComponents
                    }, null, 8, ["value"]))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("span", _hoisted_8, "By " + _toDisplayString(_unref(post).author.name), 1),
                _createVNode(_component_UAvatar, {
                  size: "2xl",
                  src: urlFor(_unref(post).author.image).width(100).height(100).url(),
                  alt: _unref(post).author.name
                }, null, 8, ["src", "alt"])
              ]),
              _createVNode(_component_BlogArticleSurround, { surround: _unref(surround) }, null, 8, ["surround"])
            ]),
            _: 1
          })
        ]),
        _: 2
      }, [
        (_unref(toc))
          ? {
              name: "right",
              fn: _withCtx(() => [
                _createVNode(_component_UPageAside, null, {
                  default: _withCtx(() => [
                    (_unref(readingTime) > 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(_unref(readingTime)) + " min read ", 1))
                      : _createCommentVNode("", true),
                    _createVNode(_component_BlogToc, {
                      links: _unref(toc),
                      highlight: "",
                      "highlight-color": "primary"
                    }, {
                      bottom: _withCtx(() => [
                        _createElementVNode("div", _hoisted_10, [
                          _createVNode(_component_USeparator, { type: "dashed" }),
                          _createVNode(_component_UPageLinks, {
                            links: _unref(socialLinks),
                            title: "Share"
                          }, null, 8, ["links"]),
                          _createVNode(_component_USeparator, { type: "dashed" })
                        ])
                      ]),
                      _: 1
                    }, 8, ["links"])
                  ]),
                  _: 1
                })
              ]),
              key: "0"
            }
          : undefined
      ]), 1024))
    : _createCommentVNode("", true)
}
}

})